import styled from '@emotion/styled'
import { ui } from '@beelday/common'
import { FC } from 'react'

/**
 * How the picture sits in the question card.
 *
 * - `compact`: a picture of a modest, fixed height under the question, with
 *   the answers close below it - online and on the participant's screen.
 * - `fill`: the picture takes all the height the card has left, keeping its
 *   proportions - the in-person stage, where the card is the projector. Only
 *   works under parents that hand it a height: see `LeadableQuizQuestion`.
 */
export type QuestionImageFit = 'compact' | 'fill'

const Container = styled.div`
	display: flex;
	flex: 1 0 50px;
	flex-flow: row nowrap;
	justify-content: center;
	margin-bottom: 10px;
`

const Image = styled.img`
	width: auto;
	height: auto;
	max-width: 60%;
	max-height: 170px;
	border-radius: 10px;
	object-fit: cover;

	${ui.responsive.desktop1440} {
		max-height: 200px;
	}
`

/**
 * `min-height: 0` lets the picture shrink with the card rather than push the
 * answers away. The picture asks for the whole height of its box and takes its
 * width from its proportions, so a small one grows to fill the space and a big
 * one shrinks to it; `contain` is for the rare picture wider than the stage,
 * which then shrinks instead of being cropped or stretched.
 */
const FillContainer = styled.div`
	display: flex;
	flex: 1 1 0;
	flex-flow: row nowrap;
	justify-content: center;
	min-height: 0;
	margin-bottom: 10px;
`

const FillImage = styled.img`
	height: 100%;
	max-width: 100%;
	border-radius: 10px;
	object-fit: contain;
`

type Props = {
	imageUrl: string
	fit: QuestionImageFit
}

const QuestionImage: FC<Props> = ({ imageUrl, fit }) => {
	return fit === 'fill' ? (
		<FillContainer>
			<FillImage src={imageUrl} />
		</FillContainer>
	) : (
		<Container>
			<Image src={imageUrl} />
		</Container>
	)
}

export default QuestionImage
